import type { ReactElement } from "react"
import { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"

import SearchIcon from "@mui/icons-material/Search"
import { CircularProgress, Grid } from "@mui/material"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import { InputText, NotificationUtils, Pagination, Typography } from "@synapse-analytics/synapse-ui"
import type { MRT_ColumnDef } from "material-react-table"

import { UploadButton } from "../../../components/UI/CustomUpload"
import { TableJsonSwitch } from "../../../components/UI/TableJsonSwitch"
import { BaseTable } from "../../../components/tables/BaseTable"
import { readFileContentAsync } from "../../../utils/genericHelpers"
import { MonacoTextEditor } from "../../data-display/MonacoTextEditor"
import { convertTableDataToJson, testJson } from "./helpers"
import type { TabularJsonTableProps } from "./interfaces"

import styles from "./TabularJsonTable.module.scss"

/**
 * Table where you can add/remove/edit test features used by script while testing
 *
 * @param {FormikProps} formik
 * @returns {ReactElement}
 */
export function TabularJsonTable<TData extends Record<string, unknown>>(
  props: Readonly<TabularJsonTableProps<TData>>,
): ReactElement {
  const { columns, data, setData, actions, rowsPerPage, page, setPage, search, setSearch, headerActions } = props

  const { t } = useTranslation("tables", {
    keyPrefix: "tabularJsonTable",
  })

  const [isJsonMode, setIsJsonMode] = useState<boolean>(false)
  const [jsonText, setJsonText] = useState<string>("")

  const onUploadFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    e.stopPropagation()
    const file = e.target.files?.[0]
    if (file) {
      try {
        const fileContent = await readFileContentAsync(file)
        setJsonText(fileContent)
      } catch (error) {
        NotificationUtils.toast(`${error}`, { title: t("notifications.uploadFile.error"), severity: "error" })
      }
    }
    e.target.value = ""
  }

  const handleSwitchMode = (): void => {
    // now we're in json mode and we want to switch to test features
    if (isJsonMode) {
      const hasPassed = testJson(jsonText, t("notifications.switchModeError"))

      if (!hasPassed) {
        return // Exit if JSON validation fails
      }

      setData(jsonText) // convert JSON to table data

      setSearch(undefined) // Clear search when switching back to table mode
      setPage(0)
    } else {
      const mappedData = data.reduce(
        (acc, feature) => ({ ...acc, [feature.name]: feature.value }),
        {} as {
          [key: string]: unknown
        },
      )

      setJsonText(convertTableDataToJson(mappedData))
    }

    setIsJsonMode(!isJsonMode)
  }

  const filteredTestFeatures = data.reduce((acc, feature) => {
    const searchMatch = !search || `${feature.name}`.toLowerCase().includes(search.toLowerCase())

    if (searchMatch) {
      acc.push({ ...feature })
    }
    return acc
  }, [] as unknown[])

  const paginatedTestFeatures = filteredTestFeatures.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  return (
    <Grid container item xs={12} pt={0.75} className={styles.outerContainer} direction={"column"} flexWrap={"nowrap"}>
      <Grid
        container
        item
        xs={isJsonMode ? 0 : 12}
        px={2}
        gap={2}
        pb={isJsonMode ? 0 : 2}
        direction={"column"}
        flexWrap={"nowrap"}
      >
        <Grid
          container
          item
          mb={isJsonMode ? 0.75 : 0}
          gap={2}
          direction={"column"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Grid container item xs justifyContent={"space-between"} alignItems={"center"} flexWrap={"nowrap"}>
            <Grid container item alignItems={"center"} gap={2}>
              <Typography variant={"a"}>{isJsonMode ? t("title.jsonEditor") : t("title.testFeatures")}</Typography>

              {isJsonMode && <UploadButton id="upload-json" accept=".json" onChange={onUploadFile} />}
            </Grid>

            <Grid container justifyContent={"flex-end"} alignItems={"center"} gap={1}>
              <TableJsonSwitch isJsonMode={isJsonMode} handleSwitchMode={handleSwitchMode} />

              {headerActions && headerActions.map((action) => action)}
            </Grid>
          </Grid>

          {!isJsonMode && (
            <Grid container item justifyContent={"space-between"} alignItems={"flex-end"}>
              <InputText
                value={search}
                id="search-bar"
                startAdornment={
                  <SearchIcon fontSize="small" style={{ marginTop: "2px", color: "var(--base-text-2)" }} />
                }
                placeholder={t("searchPlaceholder")}
                handleChange={(e: InputChangeEvent) => {
                  setSearch(e.target.value.toString())
                  setPage(0)
                }}
                variant="filled"
                hideDescription
              />

              <Grid container item gap={1} xs="auto">
                {actions.map((action) => action)}
              </Grid>
            </Grid>
          )}
        </Grid>

        {/* Test features table */}
        {!isJsonMode && (
          <Fragment>
            <BaseTable
              newStyling
              disableToolbar
              disablePagination
              enableOrdering={false}
              title={t("tableTitle")}
              pageSize={rowsPerPage}
              columns={(columns as MRT_ColumnDef[]) ?? []}
              data={paginatedTestFeatures ?? []}
              isLoading={false}
            />

            {filteredTestFeatures.length > 0 && (
              <Pagination
                hidePageSize
                variant="simple"
                size="small"
                totalCount={filteredTestFeatures.length}
                page={page + 1}
                onPageChange={(index: number) => {
                  setPage(index - 1)
                }}
                pageSize={rowsPerPage}
              />
            )}
          </Fragment>
        )}
      </Grid>

      {isJsonMode && (
        <Grid item className={styles.jsonEditorContainer}>
          <MonacoTextEditor
            value={jsonText}
            language="json"
            onChange={(text) => setJsonText(text ?? "")}
            enableCopy
            renderLineHighLight="none"
            wordWrap="on"
            width="100%"
            loading={<CircularProgress style={{ color: "rgb(25 118 210)" }} size={36} />}
            readOnly={false}
            borderRadius="0px 0px 8px 8px"
          />
        </Grid>
      )}
    </Grid>
  )
}
